import { DataTypes as DT } from 'business/definitions';
import { formatDisplay } from 'helpers/formatHelper';
import { getUrl } from 'routes/BudgetModule/components/BudgetProposal/Resource/ExpenseIncome/Operations/helpers/gridHelpers';
import { ColDef, ColGroupDef } from 'ag-grid-community';
import {
    applyDefaultColDefs,
    cellStyleSemiBold,
    buildAmountColDef,
    cellStyleBold,
} from '../colDefinition';
import {
    AMOUNT_ARBITRATED_YEAR_PREFIX,
    AMOUNT_REALIZED_YEAR_PREFIX,
    AMOUNT_REQUESTED_YEAR_PREFIX,
    AMOUNT_SUPP_YEAR_PREFIX,
    AMOUNT_VOTED_YEAR_PREFIX,
    FULL_AMOUNT_YEAR_PREFIX,
    NAME_FIELD,
} from '../types';
import { AMOUNT_FIELD, ApOperationsT, REALIZED_FIELD } from './types';
import { getDisabled } from './helper';
import { SecondaryBudgetAxisSources } from 'routes/Support/components/ClientSupport/components/BudgetConfiguration/types';
import { translateBlock } from 'i18n';

const getDateOpColumns = (
    currentYear: number,
    requestedYear: number,
    t: TranslationT,
): ColDef[] => {
    const columnsPerYear: ColDef[] = [];

    const dateColWidth = 107;
    // Requested year
    if (currentYear === requestedYear) {
        columnsPerYear.push(
            buildAmountColDef(
                `${t('viewAllAps.dateProposed')} ${currentYear}`,
                `${AMOUNT_REQUESTED_YEAR_PREFIX}..${currentYear}`,
                DT.money,
                cellStyleBold,
                false,
                dateColWidth,
            ),
        );
        columnsPerYear.push(
            buildAmountColDef(
                `${t('viewAllAps.dateArbitrated')} ${currentYear}`,
                `${AMOUNT_ARBITRATED_YEAR_PREFIX}..${currentYear}`,
                DT.money,
                cellStyleBold,
                false,
                dateColWidth,
            ),
        );
        columnsPerYear.push(
            buildAmountColDef(
                `${t('viewAllAps.dateVoted')} ${currentYear}`,
                `${AMOUNT_VOTED_YEAR_PREFIX}..${currentYear}`,
                DT.money,
                cellStyleBold,
                false,
                dateColWidth,
            ),
        );
        columnsPerYear.push(
            buildAmountColDef(
                `${t('viewAllAps.dateSupp')} ${currentYear}`,
                `${AMOUNT_SUPP_YEAR_PREFIX}..${currentYear}`,
                DT.money,
                cellStyleBold,
                false,
                dateColWidth,
            ),
        );
    }
    // Past year
    if (currentYear <= requestedYear) {
        columnsPerYear.push(
            buildAmountColDef(
                `${t('viewAllAps.fullAmount')} ${currentYear}`,
                `${FULL_AMOUNT_YEAR_PREFIX}..${currentYear}`,
                DT.money,
                cellStyleBold,
                false,
                dateColWidth,
            ),
        );
        columnsPerYear.push(
            buildAmountColDef(
                `${t('viewAllAps.dateRealized')} ${currentYear}`,
                `${AMOUNT_REALIZED_YEAR_PREFIX}..${currentYear}`,
                DT.money,
                cellStyleBold,
                false,
                dateColWidth,
            ),
        );
    }
    return columnsPerYear;
};

export const getColumns = (
    dates: number[],
    t: TranslationT,
    targetYear: number,
    sourceTable: SecondaryBudgetAxisSources | null,
): (ColDef | ColGroupDef)[] => {
    const headerColWidth = 107;
    const sourceTableLabel = translateBlock(sourceTable);
    const headers: (ColDef | ColGroupDef)[] = [
        {
            headerName: sourceTableLabel,
            field: NAME_FIELD,
            filter: 'agTextColumnFilter',
            sortable: true,
            floatingFilter: false,
            pinned: 'left',
            width: 240,
            floatingFilterComponent: 'textFloatingFilter',
            cellStyle: cellStyleSemiBold,
            cellRenderer: 'agGroupCellRenderer',
            cellRendererParams: {
                getUrl,
                getDisabled,
                innerRenderer: 'linkCellRenderer',
                style: {
                    fontWeight: '510',
                    textDecoration: 'underline',
                },
            },
            valueGetter: ({ data }: { data: ApOperationsT }) =>
                data[NAME_FIELD],
            valueFormatter: ({ data }: { data: ApOperationsT }) =>
                formatDisplay(data[NAME_FIELD], DT.text),
        },
        buildAmountColDef(
            t('viewAllAps.ressourceAmount', {
                context: sourceTable,
            }),
            `${AMOUNT_FIELD}`,
            DT.money,
            cellStyleBold,
            false,
            headerColWidth,
        ),
        buildAmountColDef(
            t('viewAllAps.ressourceRealized'),
            REALIZED_FIELD,
            DT.money,
            cellStyleBold,
            false,
            headerColWidth,
        ),
    ];
    dates.forEach((date) =>
        headers.push(...getDateOpColumns(date, targetYear, t)),
    );

    return applyDefaultColDefs(headers);
};
